import { type ComponentProps, type ReactElement, type Ref } from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

export const ToastProvider = ToastPrimitives.Provider;

export function ToastViewport({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Viewport> & {
  ref?: Ref<HTMLOListElement>;
}) {
  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={cn(
        "fixed bottom-0 right-0 z-100 flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-105",
        className,
      )}
      {...props}
    />
  );
}

export const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default:
          "border-slate-200 bg-white text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100",
        success:
          "border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100 [&_[data-toast-close]]:text-green-600 dark:[&_[data-toast-close]]:text-green-400",
        destructive:
          "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100 [&_[data-toast-close]]:text-red-600 dark:[&_[data-toast-close]]:text-red-400",
        error:
          "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100 [&_[data-toast-close]]:text-red-600 dark:[&_[data-toast-close]]:text-red-400",
        warning:
          "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100 [&_[data-toast-close]]:text-amber-600 dark:[&_[data-toast-close]]:text-amber-400",
        info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100 [&_[data-toast-close]]:text-blue-600 dark:[&_[data-toast-close]]:text-blue-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export function Toast({
  className,
  variant,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants> & {
    ref?: Ref<HTMLLIElement>;
  }) {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
}

export function ToastAction({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Action> & {
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <ToastPrimitives.Action
      ref={ref}
      className={cn(
        "inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-transparent px-3 text-sm font-medium transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:pointer-events-none disabled:opacity-50 dark:border-slate-800 dark:hover:bg-slate-800",
        className,
      )}
      {...props}
    />
  );
}

export function ToastClose({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Close> & {
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <ToastPrimitives.Close
      ref={ref}
      data-toast-close=""
      className={cn(
        "absolute right-2 top-2 rounded-md p-1 text-slate-500 opacity-0 transition-opacity hover:text-slate-900 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-primary group-hover:opacity-100 dark:text-slate-400 dark:hover:text-slate-100",
        className,
      )}
      {...props}
    >
      <X className="size-4" />
    </ToastPrimitives.Close>
  );
}

export function ToastTitle({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Title> & {
  ref?: Ref<HTMLDivElement>;
}) {
  return (
    <ToastPrimitives.Title
      ref={ref}
      className={cn("text-sm font-semibold", className)}
      {...props}
    />
  );
}

export function ToastDescription({
  className,
  ref,
  ...props
}: ComponentProps<typeof ToastPrimitives.Description> & {
  ref?: Ref<HTMLDivElement>;
}) {
  return (
    <ToastPrimitives.Description
      ref={ref}
      className={cn("text-sm opacity-90", className)}
      {...props}
    />
  );
}

export type ToastProps = ComponentProps<typeof Toast>;
export type ToastActionElement = ReactElement<typeof ToastAction>;
